// src/utils/receivedFlow.js
// Reconstructs the SMTP "Received" hop chain for the analysis UI and classifies
// each hop's trust. Shared by the three phishing-analysis views (PhishingAnalysis,
// PhishingAnalysisNAI, EmailAnalysisResult) so the logic — and the trusted-domain
// list — can't drift between them.

// Well-known mail-provider domains whose infrastructure is expected in a
// legitimate mail flow. Suffix-matched with a label boundary (see matchesDomain)
// so look-alikes like "evil-outlook.com" or "notgoogle.com" are NOT trusted.
const KNOWN_MAIL_PROVIDERS = [
  "outlook.com",
  "office.com", // covers outlook.office.com, mail.protection.outlook.com hops
  "office365.com",
  "protection.outlook.com",
  "microsoft.com",
  "microsoftonline.com",
  "hotmail.com",
  "live.com",
  "gmail.com",
  "googlemail.com",
  "google.com",
  "apple.com",
  "icloud.com",
  "yahoo.com",
  "yahoomail.com",
  "zoho.com",
  "zohomail.com",
];

/** Lowercases a Received host token and strips bracket/punctuation noise. */
export const normalizeHost = (host) => {
  if (!host) return null;
  return host
    .toLowerCase()
    .replace(/^[[(]/, "")
    .replace(/[)\],;]$/, "");
};

/** Suffix match with a label boundary: host === domain OR host ends with ".domain". */
const matchesDomain = (host, domain) => {
  if (!host || !domain) return false;
  const d = String(domain).toLowerCase();
  return host === d || host.endsWith("." + d);
};

/** True when `host` matches any domain in `list` (label-boundary suffix match). */
const matchesAny = (host, list) => !!host && list.some((d) => matchesDomain(host, d));

/** Maps a hop risk level to the pill CSS class used across the analysis views. */
export const hopPillClass = (risk) =>
  risk === "malicious"
    ? "pill-malicious"
    : risk === "danger"
    ? "pill-danger"
    : risk === "warn"
    ? "pill-warn"
    : "pill-ok";

/**
 * True when `hostDomain` belongs to a trusted source: the sender's own domain,
 * a well-known mail provider, or an admin-managed trusted domain (Protected
 * Domains + whitelisted domains/hosts, supplied by the server as `trustedDomains`).
 */
const isTrustedRelay = (hostDomain, fromDomain, trustedDomains = []) => {
  if (!hostDomain) return false;
  if (matchesDomain(hostDomain, fromDomain)) return true;
  if (KNOWN_MAIL_PROVIDERS.some((d) => matchesDomain(hostDomain, d))) return true;
  return trustedDomains.some((d) => matchesDomain(hostDomain, d));
};

/**
 * Builds the ordered hop list (origin first) with a per-hop risk classification.
 * @param {string[]} received          Raw Received header lines.
 * @param {string}   fromDomain        Sender domain (from headerSecurity).
 * @param {string[]} trustedDomains    Admin-managed trusted domains from the server.
 * @param {string[]} blacklistedDomains Admin-managed blacklisted domains from the server.
 */
export const buildReceivedFlow = (
  received = [],
  fromDomain,
  trustedDomains = [],
  blacklistedDomains = []
) => {
  if (!Array.isArray(received) || received.length === 0) return [];
  const trusted = (trustedDomains || [])
    .map((d) => String(d || "").trim().toLowerCase())
    .filter(Boolean);
  const blocked = (blacklistedDomains || [])
    .map((d) => String(d || "").trim().toLowerCase())
    .filter(Boolean);
  const reversed = [...received].reverse();

  return reversed.map((line, index) => {
    // Anchor on a word boundary so the keyword is matched even at the start of
    // the line — many Received values begin directly with "by ..." (no `from`).
    const fromMatch = line.match(/(?:^|\s)from\s+([^\s]+)/i);
    const byMatch = line.match(/(?:^|\s)by\s+([^\s]+)/i);

    const fromHost = normalizeHost(fromMatch && fromMatch[1]);
    const byHost = normalizeHost(byMatch && byMatch[1]);

    const origin = index === 0;
    const final = index === reversed.length - 1;

    // A blacklisted host anywhere in the hop (sending OR receiving) forces a
    // Malicious classification, overriding any trust — the mail demonstrably
    // traversed a known-bad domain.
    const blacklisted = matchesAny(fromHost, blocked) || matchesAny(byHost, blocked);

    // A hop is trusted if EITHER the sending (`from`) or receiving (`by`) host
    // is trusted. Inbound mail commonly shows a bare external IP in `from` but
    // is received `by` a known provider edge (e.g. *.mail.protection.outlook.com),
    // which we still want to treat as a known server rather than a red flag.
    const trustedHop =
      isTrustedRelay(fromHost, fromDomain, trusted) ||
      isTrustedRelay(byHost, fromDomain, trusted);

    let risk = "ok";
    if (blacklisted) risk = "malicious";
    else if (origin && !trustedHop) risk = "danger";
    else if (!trustedHop) risk = "warn";

    let role;
    if (origin) role = "Origin server (where the email started)";
    else if (final) role = "Final server (delivered to your mailbox)";
    else role = "Intermediate relay";

    let riskLabel;
    if (risk === "malicious") riskLabel = origin ? "Malicious origin" : "Malicious relay";
    else if (risk === "danger") riskLabel = "Suspicious origin";
    else if (risk === "warn") riskLabel = "Untrusted relay";
    else riskLabel = "Expected / known server";

    return { line, fromHost, byHost, role, risk, riskLabel };
  });
};
